export const QUARTER_IN_MONTHS = 3; // 3 months
export const BUFFERED_PERIOD_MONTHS = 3; // Default buffer for date range queries (±3 months)

export const CELL_WIDTH_REM = 2;
export const MINIMIZE_CELL_WIDTH_REM = 1.375;

// Zoom (horizontal scaling) bounds and steps. cellWidth = CELL_WIDTH_REM * zoomLevel.
export const DEFAULT_ZOOM = 1;
export const MIN_ZOOM = 0.6;
export const MAX_ZOOM = 3;
// Per wheel event — controls how fast pinch/trackpad zooming feels.
export const ZOOM_STEP = 0.05;
// Per CMD/CTRL +/- keypress — larger so taps feel snappy.
export const KEYBOARD_ZOOM_STEP = 0.2;

// Below this bar width an assignment shows only the InfoIcon (no room for text).
// Once zoom widens a bar past this threshold, the normal time+title text renders
// and the existing text-fit / sticky-label / scroll-on-hover system takes over.
export const MIN_BAR_WIDTH_FOR_TEXT_REM = 3;

// At or below this cell width the planner uses its compact display: utilization
// shown as a colored dot (instead of a percentage), single-letter weekday labels,
// and tighter assignment padding. Above it, the full display is used. This replaces
// the old `minimizeMode` boolean — compact mode is now purely a function of zoom.
// Tuned to the legacy minimize cell width so the old "minimized" look corresponds
// to zooming out to roughly that width.
export const COMPACT_CELL_WIDTH_THRESHOLD_REM = MINIMIZE_CELL_WIDTH_REM;

export const CELL_HEIGHT_PX = 74;
export const CELL_GROUP_HEIGHT_PX = 37;
export const CELL_HEIGHT_MULTIPLIER = 0.84;

export const REM_TO_PX = 16; // 1rem = 16px

// Assignment vertical positioning
export const ASSIGNMENT_LEVEL_SPACING_REM = 3; // Vertical gap between stacked assignment levels
export const ASSIGNMENT_BASE_OFFSET_REM = 2.1; // Base offset from top of row
export const ASSIGNMENT_BAR_HEIGHT_REM = 1.75; // h-7 = 1.75rem

// Tooltip positioning
export const PLANNER_TOOLTIP_WIDTH_PX = 288;
export const PLANNER_TOOLTIP_HALF_WIDTH_PX = PLANNER_TOOLTIP_WIDTH_PX / 2;

// Time-off tooltip is wider than the assignment tooltip to fit the approvers
// list (avatar + name + checkmark per approver). Used both for the tooltip's
// max width and for centering it under the cursor.
export const PLANNER_TIMEOFF_TOOLTIP_WIDTH_PX = 360;
export const PLANNER_TIMEOFF_TOOLTIP_HALF_WIDTH_PX = PLANNER_TIMEOFF_TOOLTIP_WIDTH_PX / 2;

// Horizontal pagination: Load more data when viewport is within this many days from the edge
export const HORIZONTAL_PAGINATION_THRESHOLD_DAYS = 45;

// When the viewport enters the horizontal edge buffer, shift the UI date window by this many days.
// Keep this notably smaller than HORIZONTAL_PAGINATION_THRESHOLD_DAYS so scrollbar dragging stays
// smooth even if the browser keeps partial control of scrollLeft during native thumb interaction.
export const HORIZONTAL_WINDOW_SHIFT_DAYS = 14;

// Rolling window: max months of cached data before pruning distant pages
export const MAX_CACHED_DATE_RANGE_MONTHS = 12;

// When navigating (Today / date picker) and the visible date is more than this many months
// from the target, force a full data reload instead of just scrolling. Pruned cache pages
// may leave gaps in assignment data even though queryDateRanges reports a wide range.
export const STALE_SCROLL_THRESHOLD_MONTHS = 5;

// Maximum number of days kept in the dates array at once.
// When scrolling left, new days are prepended and the same number is trimmed from the right tail
// (and vice versa), keeping scrollWidth stable so the scrollbar thumb doesn't shrink over time.
// Must be larger than 2 × HORIZONTAL_PAGINATION_THRESHOLD_DAYS + a full viewport to avoid
// trimming into visible or near-visible content. 365 days (~1 year) is a comfortable cap.
export const MAX_DATES_WINDOW = 365;

export const PLANNER_CONTAINER_ID = 'planner-container';

// Sticky sidebar width (w-56 = 14rem = 224px). Widened from 192px so most member
// names fit without truncation. MUST stay in sync with the `w-56 min-w-[14rem]` classes
// on the sidebar cell in timeline-row.tsx, group-row.tsx and timeline-header.tsx — the
// sticky assignment/time-off labels and auto-scroll are positioned from this constant.
export const PLANNER_SIDEBAR_WIDTH_PX = 224;

// Gap between sidebar and timeline (border-r on sidebar)
export const PLANNER_SIDEBAR_GAP_PX = 4;

// Combined: sticky position for assignment text and month headers
export const PLANNER_STICKY_LEFT_PX = PLANNER_SIDEBAR_WIDTH_PX + PLANNER_SIDEBAR_GAP_PX; // 228px

// Auto-scroll during drag interactions
export const AUTO_SCROLL_EDGE_ZONE_PX = 80;
export const AUTO_SCROLL_SPEED_PX = 15;

// Virtualizer / horizontal windowing
export const VIRTUALIZER_OVERSCAN_ROWS = 6;
export const HORIZONTAL_WINDOW_OVERSCAN_DAYS = 35; // Pre-render ~1 month on each side
export const HORIZONTAL_WINDOW_EDGE_BUFFER_DAYS = 15; // Trigger update when within 15 days of edge
export const MAX_VISIBLE_DATES = 150; // ~5 months max visible at once (increased for larger buffer)
export const LOAD_MORE_ROWS_THRESHOLD_PX = 300;
export const SCROLL_END_DEBOUNCE_MS = 150;
export const IDLE_CALLBACK_TIMEOUT_MS = 100;

// Minimum horizontal deltaX (px) to classify a wheel event as a horizontal swipe
export const HORIZONTAL_SWIPE_MIN_DELTA_PX = 10;

// Tolerance (in cells) for verifying scroll compensation held after pagination
export const COMPENSATION_TOLERANCE_CELLS = 2;
